import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { errorMessage } from '@/lib/errors';
import { financeExpectedByBookingQuery } from '@/lib/finance';
import { formatMinor } from '@/lib/money';
import { FINANCE_EXPECTED_STATUS_TONE } from '@/lib/status-tone';

export interface StayExpensesCardProps {
  bookingId: string;
  /** category_id → display name — the booking card has no reason to fetch
   * every category itself; the caller (bookings.$id.tsx) already has one
   * financeCategoriesQuery for the whole page. */
  categoryNames: Record<string, string>;
}

/**
 * "Stay expenses" block on a booking's Payments tab (spec §14) — every
 * finance_expected row linked to this booking (rule-generated per_stay rows
 * most commonly, but also an operator's manual entry or a routed
 * submission), history included: confirmed/skipped/cancelled rows stay
 * visible, not just the still-open ones, so an operator checking "did the
 * cleaning fee already get paid" gets an answer here instead of having to
 * cross to the queue.
 */
export function StayExpensesCard({ bookingId, categoryNames }: StayExpensesCardProps) {
  const { data, isLoading, isError, error } = useQuery(financeExpectedByBookingQuery(bookingId));
  const items = data?.items ?? [];

  return (
    <section className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-4">
      <h3 className="text-sm font-semibold uppercase tracking-[0.06em] text-muted">Stay expenses</h3>
      {isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(error)}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted">No expected payments recorded against this stay.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.06em] text-muted">
                <th className="py-1.5 pr-3 font-semibold">Article</th>
                <th className="py-1.5 pr-3 font-semibold">Amount</th>
                <th className="py-1.5 pr-3 font-semibold">Due</th>
                <th className="py-1.5 pr-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => {
                const tone = FINANCE_EXPECTED_STATUS_TONE[e.status];
                return (
                  <tr key={e.id} className="border-t border-[var(--border-hairline)]">
                    <td className="py-1.5 pr-3">
                      <Link to="/finance/expected/$id" params={{ id: e.id }} className="text-primary hover:underline">
                        {categoryNames[e.category_id] ?? e.category_id}
                      </Link>
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums">{formatMinor(e.amount_minor, e.currency)}</td>
                    <td className="py-1.5 pr-3">{e.due_date}</td>
                    <td className="py-1.5 pr-3">
                      <Badge variant={tone.variant} dot>
                        {tone.label}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
